/**
 * Auto show: runs the show with the music by changing one thing at a time (the colours, a top
 * layer, the background, the effects) on bar and phrase boundaries, and the whole look on the big
 * moments (drops, new sections). It decides *what* to change and *when*; the controller makes the
 * change. Any manual change pauses it for a few phrases.
 */
import { hash01 } from '../shared/modulation';
import { energyBucket, type EnergyTag, type MoodState, type Section } from '../shared/mood';
import type { Settings } from '../shared/settings';
import type { TransportState } from '../shared/transport';

/** One element at a time, or the whole look ('calm' strips back for a breakdown). */
export type MoveKind = 'colours' | 'top' | 'background' | 'effects' | 'whole' | 'calm';

export interface Move {
  kind: MoveKind;
  /** When the change lands: the next beat (drops), bar or phrase. */
  quantize: 'beat' | 'bar' | 'phrase';
  reason: 'timer' | 'chapter' | Section;
  /** How intense the music is now, for choosing visualizations, colours and effects. */
  energy: EnergyTag;
}

export type DirectorOpts = Pick<
  Settings,
  | 'autopilot'
  | 'autopilotBars'
  | 'autopilotOnSections'
  | 'autopilotHoldPhrases'
  | 'phraseBars'
  | 'autoColours'
  | 'autoTop'
  | 'autoBackground'
  | 'autoEffects'
  | 'autoWhole'
  | 'autoFollowEnergy'
>;

/** How often each kind of move comes up on the timer. */
const WEIGHTS: Record<'colours' | 'top' | 'background' | 'effects', number> = {
  colours: 3,
  top: 3,
  background: 2,
  effects: 1.5,
};
/** "Whole new look: often": every this many timer moves is a whole new look. */
const CHAPTER_MOVES = 6;
/** Without a locked beat, Auto show keeps time on the clock at 120 BPM in 4/4. */
const FALLBACK_BEAT_S = 0.5;
const FALLBACK_BEATS_PER_BAR = 4;

/** Beats between timer moves: the set number of bars, sooner when the music is intense. */
export function moveInterval(o: DirectorOpts, beatsPerBar: number, arousal: number): number {
  let bars = Math.max(1, o.autopilotBars);
  if (o.autoFollowEnergy) {
    const e = energyBucket(arousal);
    if (e === 'high') bars = Math.max(1, Math.round(bars / 2));
    else if (e === 'low') bars = Math.round(bars * 1.5);
  }
  return bars * Math.max(1, beatsPerBar);
}

export class Director {
  /** The beat now (the tracker's, or the clock's without a beat) and the bar length. */
  private beat = 0;
  private beatsPerBar = FALLBACK_BEATS_PER_BAR;
  private onBeatGrid: boolean | null = null;
  private lastMoveBeat: number | null = null;
  private holdUntilBeat = -Infinity;
  private lastSectionIndex: number | null = null;
  private pending = false;
  private timerMoves = 0;
  /** The most recent move kinds, newest first. */
  history: MoveKind[] = [];

  /**
   * Feed the latest transport and mood (≈10 Hz), with the time in seconds. Returns a move when
   * it's time for one.
   */
  update(
    o: DirectorOpts,
    t: TransportState,
    mood: MoodState,
    nowS: number,
    rand = Math.random,
  ): Move | null {
    this.tick(t, nowS);
    const entered = this.lastSectionIndex !== null && mood.sectionIndex !== this.lastSectionIndex;
    this.lastSectionIndex = mood.sectionIndex;
    if (!o.autopilot || this.pending) return null;
    const beat = this.beat;
    if (this.lastMoveBeat === null) this.lastMoveBeat = beat;
    if (beat < this.holdUntilBeat) return null;
    const energy: EnergyTag = o.autoFollowEnergy ? energyBucket(mood.arousal) : 'mid';

    if (entered && o.autopilotOnSections) {
      const kind = this.sectionMove(o, mood.section);
      // A drop lands on the beat; other sections on the bar.
      const quantize = mood.section === 'drop' ? 'beat' : 'bar';
      if (kind) return this.fire(beat, { kind, quantize, reason: mood.section, energy });
    }

    if (beat - this.lastMoveBeat < moveInterval(o, this.beatsPerBar, mood.arousal)) return null;
    const kind = this.timerMove(o, rand);
    if (!kind) {
      this.lastMoveBeat = beat; // nothing allowed: check again next interval
      return null;
    }
    // Big changes wait for the phrase, when moves are at least a phrase apart (so a quicker move
    // can't overtake one that's still waiting); everything else lands on the next bar.
    const big = kind === 'background' || kind === 'whole';
    const quantize = big && o.autopilotBars >= o.phraseBars ? 'phrase' : 'bar';
    return this.fire(beat, {
      kind,
      quantize,
      reason: kind === 'whole' ? 'chapter' : 'timer',
      energy,
    });
  }

  /**
   * A move right now (the V key), whether Auto show is running or not: one element, chosen as the
   * timer would (any element when none is ticked), landing on the next beat.
   */
  next(o: DirectorOpts, mood: MoodState, rand = Math.random): Move | null {
    const anyTicked = o.autoColours || o.autoTop || o.autoBackground || o.autoEffects;
    const opts: DirectorOpts = {
      ...o,
      ...(anyTicked
        ? {}
        : { autoColours: true, autoTop: true, autoBackground: true, autoEffects: true }),
      autoWhole: 'never',
    };
    const kind = this.timerMove(opts, rand);
    if (!kind) return null;
    this.history = [kind, ...this.history].slice(0, 8);
    const energy: EnergyTag = o.autoFollowEnergy ? energyBucket(mood.arousal) : 'mid';
    return { kind, quantize: 'beat', reason: 'timer', energy };
  }

  /** The move was made. */
  done(): void {
    this.pending = false;
    this.lastMoveBeat = this.beat;
  }

  /** The move couldn't be made (nothing to change): try again at the next interval. */
  skip(): void {
    this.history.shift();
    this.done();
  }

  /** You changed something yourself: hold off for a while. */
  manual(o: Pick<DirectorOpts, 'autopilotHoldPhrases' | 'phraseBars'>): void {
    this.lastMoveBeat = this.beat;
    this.pending = false;
    this.holdUntilBeat = this.beat + o.autopilotHoldPhrases * o.phraseBars * this.beatsPerBar;
  }

  reset(): void {
    this.lastMoveBeat = null;
    this.holdUntilBeat = -Infinity;
    this.pending = false;
    this.timerMoves = 0;
    this.history = [];
  }

  /** Bars until the next timer move (for the status line), or null when not running. */
  barsToNext(o: DirectorOpts, mood: MoodState): number | null {
    if (!o.autopilot || this.lastMoveBeat === null) return null;
    const hold = this.holdUntilBeat - this.beat;
    const due = this.lastMoveBeat + moveInterval(o, this.beatsPerBar, mood.arousal) - this.beat;
    return Math.ceil(Math.max(0, hold, due) / this.beatsPerBar);
  }

  /** Follow the beat grid when there is one, and the clock when there isn't. */
  private tick(t: TransportState, nowS: number): void {
    const grid = t.locked;
    const beat = grid ? t.beatIndex : Math.floor(nowS / FALLBACK_BEAT_S);
    if (this.onBeatGrid !== null && grid !== this.onBeatGrid) {
      // Switching clocks: carry the time since the last move (and any pause) across.
      const shift = beat - this.beat;
      if (this.lastMoveBeat !== null) this.lastMoveBeat += shift;
      this.holdUntilBeat += shift;
    }
    this.onBeatGrid = grid;
    this.beat = beat;
    this.beatsPerBar = grid ? Math.max(1, t.beatsPerBar) : FALLBACK_BEATS_PER_BAR;
  }

  /** What a new section calls for. */
  private sectionMove(o: DirectorOpts, section: Section): MoveKind | null {
    switch (section) {
      case 'drop':
        return o.autoWhole !== 'never'
          ? 'whole'
          : o.autoBackground
            ? 'background'
            : o.autoTop
              ? 'top'
              : null;
      case 'breakdown':
        return o.autoTop || o.autoEffects ? 'calm' : o.autoColours ? 'colours' : null;
      case 'buildup':
        return o.autoEffects ? 'effects' : o.autoColours ? 'colours' : null;
      case 'intro':
      case 'groove':
        return o.autoBackground
          ? 'background'
          : o.autoWhole !== 'never'
            ? 'whole'
            : o.autoColours
              ? 'colours'
              : null;
      default:
        return null;
    }
  }

  /** The next move on the timer: one element, never the same kind twice running. */
  private timerMove(o: DirectorOpts, rand: () => number): MoveKind | null {
    this.timerMoves++;
    if (o.autoWhole === 'often' && this.timerMoves % CHAPTER_MOVES === 0) return 'whole';
    const allowed = (Object.keys(WEIGHTS) as (keyof typeof WEIGHTS)[]).filter((k) => {
      const key = `auto${k[0]!.toUpperCase()}${k.slice(1)}` as 'autoColours';
      return o[key];
    });
    if (allowed.length === 0) return o.autoWhole === 'often' ? 'whole' : null;
    const options = allowed.length > 1 ? allowed.filter((k) => k !== this.history[0]) : allowed;
    const total = options.reduce((sum, k) => sum + WEIGHTS[k], 0);
    let r = rand() * total;
    for (const k of options) {
      r -= WEIGHTS[k];
      if (r < 0) return k;
    }
    return options.at(-1)!;
  }

  private fire(beat: number, move: Move): Move {
    this.pending = true;
    this.lastMoveBeat = beat;
    this.history = [move.kind, ...this.history].slice(0, 8);
    return move;
  }
}

/** A seeded random source for a beat, so tests (and replays) are repeatable. */
export const beatRand = (beat: number) => {
  let i = 0;
  return () => hash01(beat * 97 + i++);
};
